import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Container, Row, Button, Form, FormGroup, Label, Input, Table, Spinner } from "reactstrap";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import { Btn, H3 } from "../../../AbstractElements";
import { Plus, Trash2, ArrowLeft, Disc, Tool } from "react-feather";
import { useNavigate, useLocation } from "react-router-dom";
import { apiPost } from "../../../Utils";
import { toast, ToastContainer } from "react-toastify";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useAuth } from "../../../Context/AuthContext";

const EditLPO = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const lpoId = location.state?.id;
    const { hasPermission } = useAuth();
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [lpoType, setLpoType] = useState<'parts' | 'labour' | 'both'>('parts');
    const [sameVendor, setSameVendor] = useState(true);
    const [savedPartsData, setSavedPartsData] = useState<{
        vendor_name: string,
        new_vendor_details: string,
        items: any[]
    } | null>(null);
    const [savedLabourData, setSavedLabourData] = useState<{
        labour_vendor_name: string,
        labour_vendor_details: string,
        items: any[]
    } | null>(null);

    const { register, control, handleSubmit, watch, setValue, trigger, formState: { errors }, reset, getValues, clearErrors } = useForm({
        defaultValues: {
            id: "",
            vendor_name: "",
            new_vendor_details: "",
            labour_vendor_name: "",
            labour_vendor_details: "",
            lpo_date: "",
            branch_id: "",
            vehicle_id: "",
            odometer_reading: "",
            items: [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Parts" }],
            description: "",
            expense_category: "Spare Parts"
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    // Watch items to calculate total
    const itemValues = watch("items");
    const totalAmount = itemValues.reduce((acc, curr) => acc + (Number(curr.quantity) * Number(curr.unit_price)), 0);
    const selectedBranch = watch("branch_id");
    const watchedPartsVendor = watch("vendor_name");
    const watchedPartsDetails = watch("new_vendor_details");




    const handleTypeChange = (newType: 'parts' | 'labour' | 'both') => {
        const currentType = lpoType;
        const currentFormValues = getValues();

        // 1. Snapshot Parts Data if leaving a Parts-capable view
        let partsDataToSave = savedPartsData;

        if (currentType === 'parts' || currentType === 'both') {
            const currentPartsItems = currentFormValues.items.filter((i: any) => i.item_type === 'Parts');
            partsDataToSave = {
                vendor_name: currentFormValues.vendor_name,
                new_vendor_details: currentFormValues.new_vendor_details,
                items: currentPartsItems
            };
            setSavedPartsData(partsDataToSave);
        }

        // 2. Snapshot Labour Data if leaving a Labour-capable view
        let labourDataToSave = savedLabourData;

        if (currentType === 'labour' || currentType === 'both') {
            const currentLabourItems = currentFormValues.items.filter((i: any) => i.item_type === 'Labour');
            // Only save if it's not "Same as Parts" to avoid saving Parts info as Labour info implicitly?
            // Actually, we should save exactly what is in the labour fields.
            // If sameVendor was true, labour_vendor_name might be hidden/synced, but the underlying field value is what matters.
            // But if sameVendor is true, we usually want to clear/reset labour specific fields when we come back unless we are in 'both' mode?
            // Let's just save what is there.
            labourDataToSave = {
                labour_vendor_name: currentFormValues.labour_vendor_name,
                labour_vendor_details: currentFormValues.labour_vendor_details,
                items: currentLabourItems
            };
            setSavedLabourData(labourDataToSave);
        }

        setLpoType(newType);
        clearErrors();

        if (newType === 'parts') {
            setValue('expense_category', 'Spare Parts');

            // Restore Parts
            if (partsDataToSave) {
                setValue('vendor_name', partsDataToSave.vendor_name);
                setValue('new_vendor_details', partsDataToSave.new_vendor_details);
                const pItems = partsDataToSave.items.length ? partsDataToSave.items : [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Parts" }];
                setValue('items', pItems);
            } else {
                setValue("items", [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Parts" }]);
            }
        }
        else if (newType === 'labour') {
            setValue('expense_category', 'Maintenance');

            // Restore Labour
            if (labourDataToSave) {
                setValue('labour_vendor_name', labourDataToSave.labour_vendor_name);
                setValue('labour_vendor_details', labourDataToSave.labour_vendor_details);
                // "Empty all field" requirement only applied if we were coming from fresh start or explicitly requested clear.
                // Here we are restoring "persisted" data as per user request.
                // Assuming "sameVendor" doesn't apply in Labour Only view (it's hidden/irrelevant).
                // Actually, ensure we populate vendor_name as well if needed? No, Labour view uses 'labour_vendor_name' field?
                // Wait, logic says Labour Only view uses `labour_vendor_name` field for display?
                // Looking at JSX:
                // {(lpoType === 'labour' || lpoType === 'both') && ... <input ... {...register("labour_vendor_name", ...)}
                // So yes, we need to restore `labour_vendor_name`.
                // AND we also need to clear 'vendor_name' (Parts Vendor) to avoid confusion?
                setValue('vendor_name', '');
                setValue('new_vendor_details', '');

                const lItems = labourDataToSave.items.length ? labourDataToSave.items : [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Labour" }];
                setValue('items', lItems);
            } else {
                // Default Clean Labour
                setValue('vendor_name', '');
                setValue('new_vendor_details', '');
                setValue('labour_vendor_name', '');
                setValue('labour_vendor_details', '');
                setValue('items', [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Labour" }]);
            }
        }
        else { // type === 'both'
            setValue('expense_category', 'Maintenance');

            // Restore Parts
            if (partsDataToSave) {
                setValue('vendor_name', partsDataToSave.vendor_name);
                setValue('new_vendor_details', partsDataToSave.new_vendor_details);
            } else {
                // Ensure we at least have empty parts vendor fields if no save data
                // (Though useForm defaultValues handles this, setValue ensures overlap isn't messy)
            }

            // Restore Labour
            if (labourDataToSave) {
                // If we have saved labour data, use it.
                setValue('labour_vendor_name', labourDataToSave.labour_vendor_name);
                setValue('labour_vendor_details', labourDataToSave.labour_vendor_details);

                // If we have specific labour vendor name saved, assume they wanted different vendors?
                // Or should we respect the last 'sameVendor' state?
                // User said "labour data is persissed". If they typed something, they want it back.
                if (labourDataToSave.labour_vendor_name) {
                    setSameVendor(false);
                } else {
                    // If they had empty labour vendor name saved, maybe they were using 'Same as Parts'?
                    // Or maybe it was just empty.
                    // Let's set sameVendor to false to be safe, so they see their empty fields or data as is.
                    setSameVendor(false);
                }
            } else {
                // Default Clean Labour
                setValue('labour_vendor_name', '');
                setValue('labour_vendor_details', '');
                setSameVendor(false);
            }

            // Merge Items
            const pItems = partsDataToSave && partsDataToSave.items.length ? partsDataToSave.items : [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Parts" }];
            const lItems = labourDataToSave && labourDataToSave.items.length ? labourDataToSave.items : [{ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Labour" }];

            setValue('items', [...pItems, ...lItems]);
        }
    };

    useEffect(() => {
        if (!lpoId) {
            navigate(-1);
            return;
        }

        const loadData = async () => {
            // 1. Fetch Branches
            const brRes: any = await apiPost("branches/list", { status: 'Active' });
            if (Array.isArray(brRes)) setBranches(brRes);
            else if (brRes.data) setBranches(brRes.data);

            // 2. Fetch LPO
            const lpoRes: any = await apiPost("lpos/viewbyid", { id: lpoId });
            if (lpoRes) {
                // 3. Fetch Vehicles for the LPO's branch
                if (lpoRes.branch_id || lpoRes.vehicle?.branch_id) {
                    const branchId = lpoRes.branch_id || lpoRes.vehicle?.branch_id;
                    const vehRes: any = await apiPost("vehicles/list", { branch_id: branchId });
                    if (Array.isArray(vehRes)) {
                        const activeVehicles = vehRes.filter((v: any) => v.status !== 'Sold');
                        setVehicles(activeVehicles);
                    } else if (vehRes.data) {
                        const activeVehicles = Array.isArray(vehRes.data) ? vehRes.data.filter((v: any) => v.status !== 'Sold') : [];
                        setVehicles(activeVehicles);
                    }
                }

                // Determine initial type
                const hasParts = lpoRes.items.some((i: any) => i.item_type === 'Parts');
                const hasLabour = lpoRes.items.some((i: any) => i.item_type === 'Labour' || !i.item_type && ['Maintenance', 'Repair', 'Inspection'].includes(lpoRes.expense_category));

                if (hasParts && hasLabour) setLpoType('both');
                else if (hasLabour) setLpoType('labour');
                else setLpoType('parts');

                // 4. Pre-fill Form
                reset({
                    id: lpoRes.id,
                    vendor_name: lpoRes.vendor_name,
                    new_vendor_details: lpoRes.new_vendor_details,
                    labour_vendor_name: lpoRes.labour_vendor_name || lpoRes.vendor_name,
                    labour_vendor_details: lpoRes.labour_vendor_details || lpoRes.new_vendor_details,
                    lpo_date: lpoRes.lpo_date,
                    branch_id: lpoRes.branch_id || lpoRes.vehicle?.branch_id,
                    vehicle_id: lpoRes.vehicle_id,
                    odometer_reading: lpoRes.odometer_reading,
                    description: lpoRes.description,
                    expense_category: lpoRes.expense_category,
                    items: lpoRes.items.map((i: any) => ({
                        item_description: i.item_description,
                        quantity: i.quantity,
                        unit_price: i.unit_price,
                        part_number: i.part_number,
                        item_type: i.item_type
                    }))
                });
                if (lpoRes.labour_vendor_name && lpoRes.labour_vendor_name !== lpoRes.vendor_name) {
                    setSameVendor(false);
                } else {
                    setSameVendor(true);
                }
                setLoading(false);
            }
        };

        loadData();
    }, [lpoId]);

    // Sync Vendor Names if Same Vendor is checked
    useEffect(() => {
        if (sameVendor && lpoType === 'both') {
            setValue("labour_vendor_name", watchedPartsVendor, { shouldValidate: !!watchedPartsVendor });
            setValue("labour_vendor_details", watchedPartsDetails, { shouldValidate: !!watchedPartsVendor });
        }
    }, [sameVendor, lpoType, watchedPartsVendor, watchedPartsDetails, setValue]);

    useEffect(() => {
        // Handle branch change by user (filtering vehicles)
        if (!loading && selectedBranch) {
            apiPost("vehicles/list", { branch_id: selectedBranch }).then((res: any) => {
                if (Array.isArray(res)) {
                    const activeVehicles = res.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (res.data) {
                    const activeVehicles = Array.isArray(res.data) ? res.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                }
            });
        }
    }, [selectedBranch, loading]);

    const onSubmit = async (data: any) => {
        setSubmitting(true);
        try {


            await apiPost("lpos/update", data);
            toast.success("LPO Updated Successfully");
            setTimeout(() => navigate(`${process.env.PUBLIC_URL}/lpo`), 1000);
        } catch (error: any) {
            const msg = error.response?.data?.message || "Failed to update LPO";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Operations" title="LPO Management" pageTitle="Edit LPO" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <div className="d-flex justify-content-center mb-4">
                        <div className="btn-group">
                            <Button color={lpoType === 'parts' ? 'primary' : 'outline-primary'} onClick={() => handleTypeChange('parts')} active={lpoType === 'parts'}>
                                <Disc size={16} className="me-2" />Parts Only
                            </Button>
                            <Button color={lpoType === 'labour' ? 'primary' : 'outline-primary'} onClick={() => handleTypeChange('labour')} active={lpoType === 'labour'}>
                                <Tool size={16} className="me-2" />Labour Only
                            </Button>
                            <Button color={lpoType === 'both' ? 'primary' : 'outline-primary'} onClick={() => handleTypeChange('both')} active={lpoType === 'both'}>
                                <span className="me-2">⚡</span>Hybrid (Both)
                            </Button>
                        </div>
                    </div>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Input type="hidden" {...register("id")} />

                        {/* Common Details Card */}
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Branch <span className="text-danger">*</span></Label>
                                            <Controller
                                                control={control}
                                                name="branch_id"
                                                rules={{ required: "Branch is required" }}
                                                render={({ field }) => (
                                                    <Input type="select" className="form-select form-select-sm" {...field}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                            {errors.branch_id && <span className="text-danger small">{errors.branch_id.message as string}</span>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Vehicle <span className="text-danger">*</span></Label>
                                            <Controller
                                                control={control}
                                                name="vehicle_id"
                                                rules={{ required: "Vehicle is required" }}
                                                render={({ field }) => (
                                                    <Input type="select" className="form-select form-select-sm" {...field}>
                                                        <option value="">Select Vehicle</option>
                                                        {vehicles.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.registration_number} - {v.model}</option>
                                                        ))}
                                                    </Input>
                                                )}
                                            />
                                            {errors.vehicle_id && <span className="text-danger small">{errors.vehicle_id.message as string}</span>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>LPO Date <span className="text-danger">*</span></Label>
                                            <input type="date" className="form-control form-control-sm" {...register("lpo_date", { required: "Date required" })} />
                                            {errors.lpo_date && <span className="text-danger small">{errors.lpo_date.message as string}</span>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label>Odometer Reading</Label>
                                            <input type="number" className="form-control form-control-sm" placeholder="Current Mileage" {...register("odometer_reading")} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* Details Section */}
                        <Row>
                            {/* PARTS SECTION */}
                            {(lpoType === 'parts' || lpoType === 'both') && (
                                <Col md={lpoType === 'both' ? 6 : 12}>
                                    <Card className="border-top-primary mb-4">
                                        <CardBody>
                                            <div className="d-flex justify-content-between mb-3">
                                                <H3 className="text-primary"><Disc className="me-2" size={20} />Parts Details</H3>
                                            </div>
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Vendor Name <span className="text-danger">*</span></Label>
                                                        {/* If lpoType is both, this is the main vendor input */}
                                                        <input className="form-control form-control-sm" {...register("vendor_name", { required: "Vendor is required" })} placeholder="Vendor Name" autoComplete="off" />
                                                        {errors.vendor_name && <span className="text-danger small">{errors.vendor_name.message as string}</span>}
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Vendor Details</Label>
                                                        <input className="form-control form-control-sm" {...register("new_vendor_details")} placeholder="Address, TRN, etc." autoComplete="off" />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Category</Label>
                                                        <select className="form-select form-select-sm" {...register("expense_category")}>
                                                            {/* If Hybrid (Both), we typically use Maintenance related categories, or we can offer all depending on logic.
                                                                CreateLPO forces 'Maintenance' for Hybrid. Let's offer relevant ones. */}
                                                            {lpoType === 'both' ? (
                                                                <>
                                                                    <option value="Maintenance">Maintenance</option>
                                                                    <option value="Repair">Repair</option>
                                                                    <option value="Inspection">Inspection</option>
                                                                    <option value="Spare Parts">Spare Parts</option>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <option value="Spare Parts">Spare Parts</option>
                                                                    <option value="Tyres">Tyres</option>
                                                                    <option value="Battery">Battery</option>
                                                                    <option value="Consumables">Consumables</option>
                                                                </>
                                                            )}
                                                        </select>
                                                    </FormGroup>
                                                </Col>
                                            </Row>


                                            <Table bordered size="sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '60%' }}>Description</th>
                                                        <th style={{ width: '15%' }}>Qty</th>
                                                        <th style={{ width: '20%' }}>Price</th>
                                                        <th style={{ width: '5%' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((field: any, index) => {
                                                        const type = watch(`items.${index}.item_type` as any);
                                                        if (type && type !== 'Parts') return null;

                                                        const partsCount = (itemValues || []).filter((i: any) => i.item_type === 'Parts').length;
                                                        const itemError = (errors as any).items?.[index];

                                                        return (
                                                            <tr key={field.id}>
                                                                <input type="hidden" {...register(`items.${index}.item_type` as any)} defaultValue="Parts" />
                                                                <td>
                                                                    <input
                                                                        className={`form-control form-control-sm ${itemError?.item_description ? "is-invalid" : "border-0"}`}
                                                                        placeholder="Description"
                                                                        {...register(`items.${index}.item_description` as const, { required: "Part name is required" })} autoComplete="off"
                                                                    />
                                                                    {itemError?.item_description && <span className="text-danger small">{itemError.item_description.message}</span>}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control form-control-sm ${itemError?.quantity ? "is-invalid" : "border-0"}`}
                                                                        step="1"
                                                                        {...register(`items.${index}.quantity` as const, { required: "Required", min: { value: 1, message: "Min 1" } })}
                                                                    />
                                                                    {itemError?.quantity && <span className="text-danger small">{itemError.quantity.message}</span>}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control form-control-sm ${itemError?.unit_price ? "is-invalid" : "border-0"}`}
                                                                        step="0.01"
                                                                        {...register(`items.${index}.unit_price` as const, { required: "Price is required", min: { value: 0.0001, message: "Price should be greater than 0" } })}
                                                                    />
                                                                    {itemError?.unit_price && <span className="text-danger small">{itemError.unit_price.message}</span>}
                                                                </td>
                                                                <td>
                                                                    <Trash2
                                                                        size={14}
                                                                        className={`cursor-pointer ${partsCount > 1 ? "text-danger" : "text-muted"}`}
                                                                        onClick={() => partsCount > 1 && remove(index)}
                                                                        style={{ pointerEvents: partsCount > 1 ? 'auto' : 'none' }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                            <Button color="primary" outline size="sm" className="w-100" onClick={() => append({ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Parts" })}>
                                                <Plus size={14} /> Add Part
                                            </Button>
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}

                            {/* LABOUR SECTION */}
                            {(lpoType === 'labour' || lpoType === 'both') && (
                                <Col md={lpoType === 'both' ? 6 : 12}>
                                    <Card className="border-top-warning">
                                        <CardBody>
                                            <div className="d-flex justify-content-between mb-3">
                                                <H3 className="text-warning"><Tool className="me-2" size={20} />Labour Details</H3>
                                            </div>
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>

                                                        <div className="d-flex justify-content-between">
                                                            <Label>Vendor Name (Garage) <span className="text-danger">*</span></Label>
                                                            {lpoType === 'both' && (
                                                                <div className="form-check form-switch">
                                                                    <Input
                                                                        type="switch"
                                                                        id="sameVendor"
                                                                        checked={sameVendor}
                                                                        onChange={(e) => {
                                                                            const isChecked = e.target.checked;
                                                                            setSameVendor(isChecked);
                                                                            if (!isChecked) {
                                                                                setValue("labour_vendor_name", "");
                                                                                setValue("labour_vendor_details", "");
                                                                            }
                                                                        }}
                                                                        style={{ transform: 'scale(1.2)', borderColor: sameVendor ? '#198754' : '', backgroundColor: sameVendor ? '#198754' : '' }} autoComplete="off"
                                                                    />
                                                                    <Label check for="sameVendor" className="ms-2 text-success fw-bold" style={{ fontSize: '1.1em' }}>Same as Parts</Label>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <input className="form-control form-control-sm" {...register("labour_vendor_name", { required: lpoType === 'labour' || (lpoType === 'both' && !sameVendor) ? "Vendor is required" : false })} placeholder="Vendor Name" disabled={lpoType === 'both' && sameVendor} autoComplete="off" />
                                                        {errors.labour_vendor_name && <span className="text-danger small">{errors.labour_vendor_name.message as string}</span>}
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Vendor Details</Label>
                                                        <input className="form-control form-control-sm" {...register("labour_vendor_details")} placeholder="Address, TRN, etc." disabled={lpoType === 'both' && sameVendor} autoComplete="off" />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Category</Label>
                                                        <select className="form-select form-select-sm" {...register("expense_category")}>
                                                            <option value="Maintenance">Maintenance</option>
                                                            <option value="Repair">Repair</option>
                                                            <option value="Inspection">Inspection</option>
                                                        </select>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Table bordered size="sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '75%' }}>Description</th>
                                                        <th style={{ width: '20%' }}>Rate</th>
                                                        <th style={{ width: '5%' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((field: any, index) => {
                                                        const type = watch(`items.${index}.item_type` as any);
                                                        if (type !== 'Labour') return null;

                                                        const labourCount = (itemValues || []).filter((i: any) => i.item_type === 'Labour').length;
                                                        const itemError = (errors as any).items?.[index];

                                                        return (
                                                            <tr key={field.id}>
                                                                <input type="hidden" {...register(`items.${index}.item_type` as any)} defaultValue="Labour" />
                                                                <td>
                                                                    <input
                                                                        className={`form-control form-control-sm ${itemError?.item_description ? "is-invalid" : "border-0"}`}
                                                                        placeholder="Description"
                                                                        {...register(`items.${index}.item_description` as const, { required: "Service description is required" })} autoComplete="off"
                                                                    />
                                                                    {itemError?.item_description && <span className="text-danger small">{itemError.item_description.message}</span>}
                                                                </td>
                                                                <input type="hidden" value="1" {...register(`items.${index}.quantity` as const, { required: true })} />
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control form-control-sm ${itemError?.unit_price ? "is-invalid" : "border-0"}`}
                                                                        step="0.01"
                                                                        {...register(`items.${index}.unit_price` as const, { required: "Price is required", min: { value: 0.0001, message: "Price should be greater than 0" } })}
                                                                    />
                                                                    {itemError?.unit_price && <span className="text-danger small">{itemError.unit_price.message}</span>}
                                                                </td>
                                                                <td>
                                                                    <Trash2
                                                                        size={14}
                                                                        className={`cursor-pointer ${labourCount > 1 ? "text-danger" : "text-muted"}`}
                                                                        onClick={() => labourCount > 1 && remove(index)}
                                                                        style={{ pointerEvents: labourCount > 1 ? 'auto' : 'none' }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                            {hasPermission('lpo.edit') && (
                                                <Button color="warning" outline size="sm" className="w-100" onClick={() => append({ item_description: "", quantity: 1, unit_price: 0, part_number: "", item_type: "Labour" })}>
                                                    <Plus size={14} /> Add Service
                                                </Button>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}

                            <Col md={12}>
                                <div className="d-flex justify-content-between mt-4">
                                    <Btn color="primary" size="lg" className="px-5" disabled={submitting}>
                                        {submitting ? <>Updating <Spinner size="sm" /></> : "Update"}
                                    </Btn>
                                    <div className="text-end me-4">
                                        <small className="text-muted d-block">Total Amount</small>
                                        <span className={`fs-4 fw-bold text-primary`}>
                                            ${totalAmount.toFixed(2)}
                                        </span>
                                    </div>

                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            )
            }
            <ToastContainer />
        </div >
    );
};

export default EditLPO;
